<template>
  <div>
    <form
      v-if="data"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div :style="`height: ${contentHeight};`" class="px-3 pt-4 overflow--y">
        <CreditCard
          :creditCard="creditCard"
          :isFlipped="isFlipped"
        />

        <div class="payment-method-form">
          <div
            class="form-group__flag"
            :class="creditCard.flag ? `form-group__flag--${creditCard.flag}` : null"
          >
            <label for="">{{ $t(`${namespace}.creditCardNumber`) }}</label>
            <v-text-field
              v-model="creditCard.number"
              type="tel"
              placeholder="•••• •••• •••• ••••"
              v-validate="{ required: true, regex: /[\d\s]/ }"
              :error-messages="errors.collect($t(`${namespace}.creditCardNumber`))"
              :data-vv-name="$t(`${namespace}.creditCardNumber`)"
              required
              flat
              outline
              solo
              color="primary"
              :mask="creditCardMask"
              @keyup="getCardFlag"
            />
          </div>

          <div>
            <label for="">{{ $t(`${namespace}.creditCardName`) }}</label>
            <v-text-field
              v-model="creditCard.name"
              :placeholder="`${$t(`${namespace}.printedOnCard`)}`"
              v-validate="{ required: true, regex: /^[A-z0-9][A-z0-9\s]+[A-z0-9]$/ }"
              :error-messages="errors.collect($t(`${namespace}.creditCardName`))"
              :data-vv-name="$t(`${namespace}.creditCardName`)"
              required
              flat
              outline
              solo
              color="primary"
            />
          </div>

          <v-layout>
            <v-flex xs6>
              <div class="pr-2">
                <label for="">{{ $t(`${namespace}.creditCardDate`) }}</label>
                <v-text-field
                  v-model="creditCard.validate"
                  type="tel"
                  :placeholder="`${$t(`${namespace}.creditCardDateFormat`)}`"
                  v-validate="'required'"
                  :error-messages="errors.collect($t(`${namespace}.creditCardDate`))"
                  :data-vv-name="$t(`${namespace}.creditCardDate`)"
                  required
                  flat
                  outline
                  solo
                  color="primary"
                  :rules="dateRules"
                  mask="##/##"
                  @input="formatCreditCardDate"
                />
              </div>
            </v-flex>

            <v-flex xs6>
              <div class="pl-2 cvv">
                <label for="">{{ $t(`${namespace}.creditCardCvv`) }}</label>
                <v-text-field
                  v-model="creditCard.cvv"
                  type="tel"
                  placeholder="•••"
                  :maxlength="creditCard.flag === 'amex' ? '4' : '3'"
                  v-validate="creditCard.flag === 'amex' ? 'required|min:4|max:4|numeric' : 'required|min:3|max:3|numeric'"
                  :error-messages="errors.collect($t(`${namespace}.creditCardCvv`))"
                  :data-vv-name="$t(`${namespace}.creditCardCvv`)"
                  required
                  flat
                  outline
                  solo
                  color="primary"
                  @focus="flip"
                  @blur="flip"
                >
                  <v-tooltip slot="append" top max-width="196">
                    <v-icon slot="activator"></v-icon>
                    <span>{{ $t(`${namespace}.cvvMessage`) }}</span>
                  </v-tooltip>
                </v-text-field>
              </div>
            </v-flex>
          </v-layout>

          <p v-html="getCurrencyInString($t(`${namespace}.discountMessage`))" class="caption"></p>
        </div>
      </div>

      <v-divider />

      <div class="pt-4 pb-3">
        <div class="text-xs-center">
          <v-btn
            type="submit"
            :disabled="isLoading || isDisabled"
            :loading="isLoading"
            round
            depressed
            color="primary"
          >
            {{ $t('Common.proceed') }}
          </v-btn>
        </div>

        <div class="text-xs-center mt-3">
          <a
            v-if="!isLoading"
            href="#"
            class="accent--text"
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>
        </div>
      </div>
    </form>

    <ChangePaymentMethodDialog
      :dialog="dialog.visibility"
      :status="dialog.status"
      @closeDialog="closeDialog"
      @closeDrawer="close"
    />
  </div>
</template>

<script>
import moment from 'libs/moment'
import { cardType } from '@polvo-labs/card-type'
import { mapState, mapActions } from 'vuex'
import adyen from 'corp-front-vue-commons/src/utils/adyen'
import { calcContentHeight, catchError } from 'corp-front-vue-commons/src/utils/functions'
import CreditCard from './CreditCard'
import ChangePaymentMethodDialog from './ChangePaymentMethodDialog'

export default {
  name: 'Edit',
  props: {
    data: {
      type: Object
    }
  },
  components: {
    CreditCard,
    ChangePaymentMethodDialog
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    dateRules () {
      function isValidDate (val) {
        if (!val || val.length < 4) return false
        const arr = /(\d{2})(\d{2})/.exec(val)
        const d = '01'
        const m = arr[1]
        const y = `20${arr[2]}`
        const inputDate = moment(`${y}${m}${d}`).format('YYYY-MM-DD')
        const actualDate = moment().format('YYYY-MM-DD')
        const diff = Math.round(moment(new Date(inputDate)).diff(new Date(actualDate), 'months'))
        if (diff < 0 || inputDate === 'Invalid date') return false
        return true
      }

      return [
        v => !!isValidDate(v) || this.$t(`${this.namespace}.creditCardDateError`)
      ]
    }
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.paymentMethod.form',
      isLoading: false,
      isDisabled: false,
      contentHeight: 'none',
      isFlipped: false,
      creditCardMask: '#### #### #### ####',
      creditCard: {
        number: null,
        numberFormated: null,
        name: null,
        validate: null,
        validateFormated: null,
        cvv: null,
        flag: null
      },
      dialog: {
        visibility: false,
        status: 'loading'
      },
      isFetching: true
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    calcContentHeight () {
      this.contentHeight = `${calcContentHeight() - 125}px`
    },

    close () {
      this.$emit('close')
    },

    openDialog (status) {
      this.dialog = {
        visibility: true,
        status
      }
    },

    closeDialog () {
      this.dialog = {
        visibility: false,
        status: null
      }
    },

    getCurrencyInString (val) {
      const regex = /(R\$\s?(?:\d{1,3}\.)*(?:\d{1,3})(?:,\d{2}))/
      return val.replace(regex, '<strong>$1</strong>')
    },

    adyenEncryptForm () {
      const { number, name, validateFormated, cvv } = this.creditCard
      const expiryMonth = validateFormated.split('/')[0]
      const expiryYear = `20${validateFormated.split('/')[1]}`

      return adyen.createEncryption(process.env.VUE_APP_ADYEN_KEY, {}).encrypt({
        number,
        cvc: cvv,
        holderName: name.toUpperCase(),
        generationtime: new Date().toISOString(),
        expiryMonth,
        expiryYear
      })
    },

    flip () {
      this.isFlipped = !this.isFlipped
    },

    applyMask (flag) {
      const maskPatterns = {
        mastercard: '#### #### #### ####',
        visa: '#### #### #### ####',
        amex: '#### ###### #####',
        hipercard: '',
        elo: '#### #### #### ####',

        // not accept
        diners: '#### ###### ####',
        discover: '#### #### #### ####',
        jcb: '#### #### #### ####'
      }

      this.creditCardMask = maskPatterns[flag] || '#### #### #### ####'
    },

    countChars (flag) {
      const flagList = {
        mastercard: { minlength: 16, maxlength: 16 },
        visa: { minlength: 13, maxlength: 16 },
        amex: { minlength: 15, maxlength: 15 },
        hipercard: { minlength: 13, maxlength: 19 },
        elo: { minlength: 16, maxlength: 16 },

        // not accept
        diners: { minlength: 14, maxlength: 16 },
        discover: { minlength: 16, maxlength: 16 },
        jcb: { minlength: 16, maxlength: 16 }
      }

      if (flag) this.creditCard.flag = flag
      return flagList[flag] || flagList.mastercard
    },

    getCardFlag (e) {
      const el = e.target
      const flag = cardType(el.value)
      this.creditCard.numberFormated = el.value

      if (!flag) {
        this.creditCard.flag = null
        this.creditCardMask = '#### #### #### ####'
        return
      }

      const { minlength, maxlength } = this.countChars(flag)
      el.setAttribute('minlength', minlength + 3)
      el.setAttribute('maxlength', maxlength + 3)
      this.applyMask(flag)
    },

    formatCreditCardDate () {
      this.creditCard.validateFormated = this.creditCard.validate.replace(/(\d{2})(\d{2})/, '$1/$2')
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()

        if (!isValid) {
          const elem = this.$el.querySelector(`[data-vv-name="${this.$validator.errors.items[0].field}"]`)
          elem.scrollIntoView(false)
          elem.focus()
          return
        }

        this.isLoading = true
        this.openDialog('loading')

        const cardEncrypted = this.adyenEncryptForm()
        const companyId = this.company ? this.company.id : null
        const oldCreditCard = this.data.oldCreditCard || null
        const { data: res } = await this.$apiService.changeCreditCard({
          cardEncrypted,
          companyId,
          oldCreditCard
        })

        if (!res.valid) {
          this.openDialog('error')
        } else {
          this.close()
          this.successAlert(this.$t(`${this.namespace}.paymentChangedSuccess`))
        }
      } catch (error) {
        catchError(error)
        this.errorAlert(this.$t('Messages.genericError'))
        this.closeDialog()
      }

      this.isLoading = false
    }
  },
  mounted () {
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss">
.payment-method-form {

  // flags
  .form-group__flag {
    position: relative;

    &:before {
      content: "";
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100%;
      height: 40px;
      position: absolute;
      right: 5px;
      transform: translateY(-50%);
      top: 49px;
      width: 40px;
      z-index: 1;
    }
  }

  .form-group__flag--mastercard:before {
    background-image: url('~@/assets/img/payment-methods/master-card.svg');
  }

  .form-group__flag--visa:before {
    background-image: url('~@/assets/img/payment-methods/visa.svg');
  }

  .form-group__flag--amex:before {
    background-image: url('~@/assets/img/payment-methods/amex.svg');
    background-size: 50%;
  }

  .form-group__flag--hipercard:before {
    background-image: url('~@/assets/img/payment-methods/hipercard.svg');
  }

  .form-group__flag--elo:before {
    background-image: url('~@/assets/img/payment-methods/elo.svg');
  }

  .form-group__flag--diners:before {
    background-image: url('~@/assets/img/payment-methods/diners.svg');
  }

  .financial-drawer-content {
    overflow-x: initial;
    overflow-y: auto;
  }
}

.cvv {
  position: relative;

  .v-icon {
    background: url('~@/assets/img/img-cvv.svg') center no-repeat;
    height: 24px;
    width: 24px;
  }
}
</style>
